import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Satellite, Mountain, Waypoints, ShieldCheck, X, Gauge } from "lucide-react";

const SENSOR_LABEL = {
  "sentinel-2": "Sentinel-2 (optical)",
  "sentinel-1": "Sentinel-1 (SAR)",
  "cop-dem": "Copernicus DEM",
  aster: "ASTER (SWIR)",
  enmap: "EnMAP (hyperspectral)",
  landsat: "Landsat 8/9",
};

const priorityClass = (p) =>
  p === "Priority 1"
    ? "bg-red-100 text-red-700 border-red-200"
    : p === "Priority 2"
    ? "bg-red-100 text-red-700 border-red-200"
    : p === "Priority 3"
    ? "bg-sky-100 text-sky-700 border-sky-200"
    : "bg-slate-100 text-slate-600 border-slate-200";

const confClass = (s) =>
  s >= 80 ? "text-emerald-700" : s >= 55 ? "text-red-600" : "text-red-600";

function EvidenceChip({ icon: Icon, label, score }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] text-slate-700">
      <Icon size={12} className="text-orange-500" />
      {label}
      {score != null && <span className="font-semibold tabular-nums">{Math.round(score)}/100</span>}
    </span>
  );
}

export default function TargetStoryDialog({ open, onOpenChange, areaName, targets = [], evidence }) {
  const sensors = evidence?.sensors || [];
  const hasStruct = evidence?.structural;
  const hasTerrain = evidence?.terrain;
  const sorted = [...targets].sort(
    (a, b) => (b.prospectivity_score || 0) - (a.prospectivity_score || 0)
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl w-[92vw] h-[90vh] p-0 gap-0 flex flex-col overflow-hidden [&>button]:hidden"
        data-testid="target-story-dialog"
      >
        <div className="flex items-center justify-between px-5 h-14 border-b border-slate-200 bg-white shrink-0">
          <DialogTitle
            className="text-sm font-bold text-slate-900 flex items-center gap-2"
            style={{ fontFamily: "Manrope, sans-serif" }}
          >
            <ShieldCheck size={16} className="text-orange-500" />
            Confidence Storytelling - {areaName}
          </DialogTitle>
          <button
            data-testid="story-close"
            onClick={() => onOpenChange(false)}
            className="h-8 w-8 grid place-items-center rounded-md text-slate-600 hover:bg-slate-100"
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-auto p-5 bg-slate-50 space-y-4">
          {evidence && (
            <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="area-evidence-summary">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                Evidence base for this area
              </div>
              <div className="flex flex-wrap gap-2">
                {sensors.map((s) => (
                  <span key={s} className="inline-flex items-center gap-1.5 rounded-full bg-orange-50 border border-orange-200 px-2.5 py-1 text-[11px] font-medium text-orange-700">
                    <Satellite size={12} /> {SENSOR_LABEL[s] || s}
                  </span>
                ))}
                <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-50 border border-slate-200 px-2.5 py-1 text-[11px] text-slate-600">
                  {evidence.streams_available}/{evidence.streams_expected} evidence streams · {evidence.coverage_pct}% coverage · mean confidence{" "}
                  <span className={`font-semibold ${confClass(evidence.confidence_mean || 0)}`}>
                    {Math.round(evidence.confidence_mean || 0)}/100
                  </span>
                </span>
              </div>
              {(() => {
                const rec = (evidence.recommended_sensors || []).filter((s) => !sensors.includes(s));
                return rec.length ? (
                  <div className="mt-2 text-[11px] text-slate-400">
                    Recommended for enhancement: {rec.map((s) => SENSOR_LABEL[s] || s).join(", ")}
                  </div>
                ) : null;
              })()}
            </div>
          )}

          {sorted.length === 0 && (
            <div className="text-sm text-slate-500 text-center py-10">No targets for this area.</div>
          )}

          {sorted.map((t) => (
            <div
              key={t.global_id || t.target_id}
              className="rounded-xl border border-slate-200 bg-white p-4"
              data-testid={`target-story-${t.global_id || t.local_label}`}
            >
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <span className="font-bold text-slate-900">{t.local_label || t.global_id}</span>
                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${priorityClass(t.priority)}`}>
                    {t.priority}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-xs">
                  <span className="flex items-center gap-1 text-slate-600">
                    <Gauge size={13} className="text-orange-500" /> Prospectivity{" "}
                    <b className="tabular-nums text-slate-900">{t.prospectivity_score}/100</b>
                  </span>
                  <span className="flex items-center gap-1 text-slate-600">
                    Confidence{" "}
                    <b className={`tabular-nums ${confClass(t.confidence_score || 0)}`}>
                      {t.confidence_score}/100
                    </b>
                    <span className="text-slate-400">({t.confidence})</span>
                  </span>
                </div>
              </div>

              {t.deposit_model && (
                <div className="mt-1 text-[11px] text-slate-500">Model: {t.deposit_model}</div>
              )}
              {t.rationale && <p className="mt-2 text-sm text-slate-700 leading-relaxed">{t.rationale}</p>}

              <div className="mt-3">
                <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
                  Evidence provided by
                </div>
                <div className="flex flex-wrap gap-2">
                  <EvidenceChip icon={Satellite} label="Optical spectral (Sentinel-2)" score={t.spectral_score} />
                  {hasStruct && (
                    <EvidenceChip icon={Waypoints} label="Structural (lineaments)" score={t.structural_score} />
                  )}
                  {hasTerrain && <EvidenceChip icon={Mountain} label="Terrain (Copernicus DEM)" />}
                  <EvidenceChip icon={ShieldCheck} label="Data confidence" score={t.confidence_score} />
                </div>
              </div>
            </div>
          ))}

          <p className="text-[11px] text-slate-400 pt-1">
            Exploration targets only - confidence reflects how many independent sensors and how much valid imagery
            support each anomaly. Field verification is required before any resource significance.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
